import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  Camera,
  Upload,
  Video,
  Image as ImageIcon,
  Mic,
  Wand2,
  Send,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Play,
  Pause,
  Trash2,
  Download,
  Users,
  Shield,
  DollarSign,
  Phone,
  MessageSquare,
  Mail,
  Globe,
  FileVideo,
  Eye,
  EyeOff,
  Lock,
  Sparkles,
  Radio
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Delivery channels
const deliveryChannels = [
  { id: "sms", name: "SMS/Text", icon: <Phone className="w-4 h-4" />, cost: 2 },
  { id: "whatsapp", name: "WhatsApp", icon: <MessageSquare className="w-4 h-4" />, cost: 2 },
  { id: "email", name: "Email", icon: <Mail className="w-4 h-4" />, cost: 1 },
  { id: "ptt", name: "Push to Talk", icon: <Radio className="w-4 h-4" />, cost: 3 },
  { id: "direct", name: "Direct Message", icon: <Send className="w-4 h-4" />, cost: 1 }
];

// Content creation modes
const creationModes = [
  { id: "record", name: "Record Video", icon: <Camera className="w-6 h-6" />, desc: "Use camera to record" },
  { id: "upload", name: "Upload File", icon: <Upload className="w-6 h-6" />, desc: "Upload video/image" },
  { id: "ai_video", name: "AI Video", icon: <Wand2 className="w-6 h-6" />, desc: "Text to video" },
  { id: "ai_image", name: "AI Image", icon: <Sparkles className="w-6 h-6" />, desc: "Text to image" },
  { id: "photo_video", name: "Photo → Video", icon: <FileVideo className="w-6 h-6" />, desc: "Convert image to video" }
];

export default function VidMe() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  // Steps
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Content Creation
  const [creationMode, setCreationMode] = useState(null);
  const [contentFile, setContentFile] = useState(null);
  const [contentPreview, setContentPreview] = useState(null);
  const [contentType, setContentType] = useState(null); // 'video' or 'image'
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  // Step 2: Moderation
  const [moderationResult, setModerationResult] = useState(null);
  const [moderating, setModeratin] = useState(false);

  // Step 3: Recipients
  const [recipients, setRecipients] = useState([{ id: 1, name: "", contact: "", channel: "email" }]);
  const [selectedChannels, setSelectedChannels] = useState(["email"]);

  // Step 4: Review & Send
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sentResults, setSentResults] = useState(null);

  // Compliance
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [allowDelete, setAllowDelete] = useState(true);

  // Calculate costs
  const calculateCost = () => {
    let cost = 5; // Base cost for video processing
    recipients.forEach(r => {
      const channel = deliveryChannels.find(c => c.id === r.channel);
      if (channel) cost += channel.cost;
    });
    return cost;
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");

    if (!isVideo && !isImage) {
      toast.error("Please upload a video or image file");
      return;
    }

    setContentFile(file);
    setContentType(isVideo ? "video" : "image");
    setContentPreview(URL.createObjectURL(file));
    toast.success(`${isVideo ? "Video" : "Image"} uploaded successfully`);
  };

  // Start camera recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      videoRef.current.srcObject = stream;
      videoRef.current.play();

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        setContentFile(blob);
        setContentType("video");
        setContentPreview(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info("Recording started...");
    } catch (error) {
      toast.error("Camera access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success("Recording saved!");
    }
  };

  // Generate AI content
  const generateAIContent = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Enter a description for AI generation");
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (creationMode === "ai_video" || creationMode === "photo_video") {
        // Use photo-to-video endpoint
        const response = await axios.post(`${API}/photo-to-video`, {
          product_name: aiPrompt,
          video_type: "sales",
          tone: "professional",
          duration: 30
        }, { headers });

        toast.success("Video project created! Generating assets...");
        // For now, set as pending
        setContentType("video");
        setContentPreview(null);
        setModerationResult({ passed: true, action: "approved" });
        setCurrentStep(3);
      } else if (creationMode === "ai_image") {
        const response = await axios.post(`${API}/images/generate`, {
          prompt: aiPrompt,
          provider: "openai",
          style: "cinematic"
        }, { headers });

        if (response.data.url) {
          setContentType("image");
          setContentPreview(`${process.env.REACT_APP_BACKEND_URL}${response.data.url}`);
          toast.success("Image generated!");
        }
      }
    } catch (error) {
      toast.error("Generation failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  // Run content moderation
  const runModeration = async () => {
    setModeratin(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Check text content
      const textToCheck = message + " " + aiPrompt;
      const response = await axios.post(
        `${API}/moderation/check?text=${encodeURIComponent(textToCheck)}&content_type=vidme`,
        {},
        { headers }
      );

      setModerationResult(response.data);

      if (response.data.passed) {
        toast.success("Content approved! Ready to send.");
        setCurrentStep(3);
      } else {
        toast.error("Content flagged: " + response.data.flagged_categories.join(", "));
      }
    } catch (error) {
      // If moderation fails, allow with warning
      setModerationResult({ passed: true, action: "approved", warning: "Moderation service unavailable" });
      setCurrentStep(3);
    } finally {
      setModeratin(false);
    }
  };

  // Add recipient
  const addRecipient = () => {
    setRecipients([...recipients, { 
      id: Date.now(), 
      name: "", 
      contact: "", 
      channel: "email" 
    }]);
  };

  const updateRecipient = (id, field, value) => {
    setRecipients(recipients.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const removeRecipient = (id) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter(r => r.id !== id));
    }
  };

  // Send content
  const sendContent = async () => {
    if (!agreeTerms) {
      toast.error("Please agree to the terms and compliance controls");
      return;
    }

    if (recipients.some(r => !r.contact.trim())) {
      toast.error("Please fill in all recipient contacts");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Create VidMe delivery record
      const vidmeRecord = {
        content_type: contentType,
        message: message,
        recipients: recipients,
        allow_delete: allowDelete,
        moderation_result: moderationResult,
        cost_credits: calculateCost()
      };

      // Use smart routing for delivery
      const contactsForRouting = recipients.map(r => ({
        id: r.id.toString(),
        name: r.name,
        email: r.channel === "email" ? r.contact : "",
        phone: ["sms", "whatsapp", "ptt"].includes(r.channel) ? r.contact : "",
        phone_verified: r.channel === "sms",
        email_verified: r.channel === "email"
      }));

      const response = await axios.post(`${API}/broadcast/smart-route`, {
        contacts: contactsForRouting,
        message: message || "Check out this video!",
        fallback_to_email: true
      }, { headers });

      setSentResults(response.data);
      toast.success(`Sent to ${response.data.successful} recipient(s)!`);
      setCurrentStep(4);
    } catch (error) {
      toast.error("Delivery failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setSending(false);
    }
  };

  // Delete sent content (placeholder)
  const deleteContent = async () => {
    toast.info("Delete request sent. Recipients will be notified.");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold text-text-primary">VidMe</span>
          </div>
          {user && (
            <div className="text-sm text-text-muted">
              <span className="text-primary font-bold">{user.credits}</span> credits
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                currentStep >= step 
                  ? "bg-primary text-white" 
                  : "bg-background-paper border border-border text-text-muted"
              }`}>
                {currentStep > step ? <CheckCircle className="w-4 h-4" /> : step}
              </div>
              {step < 4 && (
                <div className={`w-12 h-1 mx-1 ${
                  currentStep > step ? "bg-primary" : "bg-border"
                }`} />
              )}
            </div>
          ))}
        </div>

        <div className="text-center mb-6">
          <h2 className="font-heading font-bold text-xl text-text-primary">
            {currentStep === 1 && "Step 1: Create Your Content"}
            {currentStep === 2 && "Step 2: Content Review & Moderation"}
            {currentStep === 3 && "Step 3: Choose Recipients"}
            {currentStep === 4 && "Step 4: Delivery Complete"}
          </h2>
        </div>

        <AnimatePresence mode="wait">
          {/* STEP 1: Content Creation */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Creation Mode Selection */}
              {!creationMode && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {creationModes.map((mode) => (
                    <Card
                      key={mode.id}
                      onClick={() => setCreationMode(mode.id)}
                      className="cursor-pointer bg-background-paper border-border hover:border-primary transition-all"
                    >
                      <CardContent className="p-4 text-center">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-2 text-primary">
                          {mode.icon}
                        </div>
                        <div className="text-sm font-medium text-text-primary">{mode.name}</div>
                        <div className="text-xs text-text-muted">{mode.desc}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Record Mode */}
              {creationMode === "record" && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6">
                    <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4 relative">
                      <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
                      {isRecording && (
                        <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1 rounded-full bg-red-500 text-white text-sm">
                          <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                          Recording
                        </div>
                      )}
                    </div>
                    <div className="flex justify-center gap-3">
                      {!isRecording ? (
                        <Button onClick={startRecording} className="bg-red-500 hover:bg-red-600 text-white">
                          <Camera className="w-4 h-4 mr-2" />
                          Start Recording
                        </Button>
                      ) : (
                        <Button onClick={stopRecording} variant="outline" className="border-red-500 text-red-500">
                          <Pause className="w-4 h-4 mr-2" />
                          Stop Recording
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Upload Mode */}
              {creationMode === "upload" && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6">
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-border rounded-xl p-12 text-center cursor-pointer hover:border-primary/50 transition-all"
                    >
                      <Upload className="w-12 h-12 text-text-muted mx-auto mb-4" />
                      <p className="text-text-primary font-medium">Click to upload video or image</p>
                      <p className="text-xs text-text-muted mt-1">MP4, MOV, JPG, PNG up to 100MB</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/*,image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </CardContent>
                </Card>
              )}

              {/* AI Generation Modes */}
              {["ai_video", "ai_image", "photo_video"].includes(creationMode) && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6 space-y-4">
                    <div>
                      <Label className="text-text-primary mb-2 block">
                        {creationMode === "ai_image" ? "Describe the image you want" : "Describe your video"}
                      </Label>
                      <Textarea
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder={creationMode === "ai_image" 
                          ? "A luxury Rolex Submariner watch on a marble surface with dramatic lighting..."
                          : "Create a sales video for a luxury Rolex watch, highlighting its craftsmanship..."
                        }
                        className="bg-background border-border text-text-primary"
                        rows={4}
                      />
                    </div>
                    <Button
                      onClick={generateAIContent}
                      disabled={generating || !aiPrompt.trim()}
                      className="w-full bg-gradient-to-r from-primary to-accent-neon text-white"
                    >
                      {generating ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                      ) : (
                        <><Wand2 className="w-4 h-4 mr-2" />Generate with AI</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Content Preview */}
              {contentPreview && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-4">
                    <Label className="text-text-muted text-xs mb-2 block">Preview</Label>
                    {contentType === "video" ? (
                      <video src={contentPreview} controls className="w-full rounded-lg max-h-64" />
                    ) : (
                      <img src={contentPreview} alt="Preview" className="w-full rounded-lg max-h-64 object-contain" />
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Navigation */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCreationMode(null);
                    setContentFile(null);
                    setContentPreview(null);
                  }}
                  className="border-border"
                >
                  Reset
                </Button>
                <Button
                  onClick={() => setCurrentStep(2)}
                  disabled={!contentPreview && !aiPrompt}
                  className="bg-primary hover:bg-primary-hover text-white"
                >
                  Next: Review Content
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Moderation */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium text-text-primary">Content Compliance Check</h3>
                      <p className="text-xs text-text-muted">We scan for prohibited content before delivery</p>
                    </div>
                  </div>

                  {/* What we check */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded bg-background border border-border flex items-center gap-2">
                      <EyeOff className="w-3 h-3 text-accent-error" />
                      <span className="text-text-muted">Adult/Nudity</span>
                    </div>
                    <div className="p-2 rounded bg-background border border-border flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3 text-accent-warning" />
                      <span className="text-text-muted">Violence</span>
                    </div>
                    <div className="p-2 rounded bg-background border border-border flex items-center gap-2">
                      <Lock className="w-3 h-3 text-accent-error" />
                      <span className="text-text-muted">Illegal Content</span>
                    </div>
                    <div className="p-2 rounded bg-background border border-border flex items-center gap-2">
                      <Shield className="w-3 h-3 text-primary" />
                      <span className="text-text-muted">Hate Speech</span>
                    </div>
                  </div>

                  {/* Add message */}
                  <div>
                    <Label className="text-text-muted text-xs mb-1 block">Add a message (optional)</Label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Add a personal message to your recipients..."
                      className="bg-background border-border text-text-primary"
                      rows={2}
                    />
                  </div>

                  {/* Moderation Result */}
                  {moderationResult && (
                    <div className={`p-4 rounded-lg ${
                      moderationResult.passed 
                        ? "bg-accent-success/10 border border-accent-success/30" 
                        : "bg-accent-error/10 border border-accent-error/30"
                    }`}>
                      <div className="flex items-center gap-2">
                        {moderationResult.passed ? (
                          <CheckCircle className="w-5 h-5 text-accent-success" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-accent-error" />
                        )}
                        <span className={moderationResult.passed ? "text-accent-success" : "text-accent-error"}>
                          {moderationResult.passed ? "Content Approved" : "Content Flagged"}
                        </span>
                      </div>
                      {!moderationResult.passed && (
                        <p className="text-xs text-text-muted mt-2">
                          Flagged categories: {moderationResult.flagged_categories?.join(", ")}
                        </p>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={runModeration}
                    disabled={moderating}
                    className="w-full bg-primary hover:bg-primary-hover text-white"
                  >
                    {moderating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning Content...</>
                    ) : (
                      <><Shield className="w-4 h-4 mr-2" />Run Compliance Check</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(1)} className="border-border">
                  Back
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Recipients */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-text-primary font-medium">Recipients</Label>
                    <Button size="sm" variant="outline" onClick={addRecipient} className="text-xs">
                      + Add Recipient
                    </Button>
                  </div>

                  {recipients.map((recipient, index) => (
                    <div key={recipient.id} className="p-3 rounded-lg bg-background border border-border space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          value={recipient.name}
                          onChange={(e) => updateRecipient(recipient.id, "name", e.target.value)}
                          placeholder="Name (optional)"
                          className="bg-background-subtle border-border text-text-primary text-sm flex-1"
                        />
                        {recipients.length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeRecipient(recipient.id)}
                            className="text-accent-error h-8 px-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={recipient.channel}
                          onChange={(e) => updateRecipient(recipient.id, "channel", e.target.value)}
                          className="px-3 py-2 rounded-lg bg-background-subtle border border-border text-text-primary text-sm"
                        >
                          {deliveryChannels.map((ch) => (
                            <option key={ch.id} value={ch.id}>{ch.name} ({ch.cost} credits)</option>
                          ))}
                        </select>
                        <Input
                          value={recipient.contact}
                          onChange={(e) => updateRecipient(recipient.id, "contact", e.target.value)}
                          placeholder={recipient.channel === "email" ? "email@example.com" : "+1234567890"}
                          className="bg-background-subtle border-border text-text-primary text-sm flex-1"
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Compliance & Terms */}
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6 space-y-4">
                  <Label className="text-text-primary font-medium">Compliance Controls</Label>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                    <div className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4 text-text-muted" />
                      <span className="text-sm text-text-primary">Allow content deletion</span>
                    </div>
                    <Switch checked={allowDelete} onCheckedChange={setAllowDelete} />
                  </div>

                  <div className="p-3 rounded-lg bg-background border border-border">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                        className="mt-1"
                      />
                      <span className="text-xs text-text-muted">
                        I agree to comply with all applicable telecommunications regulations, anti-spam laws (CAN-SPAM, TCPA, GDPR), 
                        and platform terms of service. I confirm this content is not unsolicited and recipients have consented to receive communications.
                      </span>
                    </label>
                  </div>

                  {/* Cost Summary */}
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                    <div className="flex items-center justify-between">
                      <span className="text-text-primary font-medium">Total Cost</span>
                      <span className="text-primary font-bold text-lg">{calculateCost()} credits</span>
                    </div>
                    <p className="text-xs text-text-muted mt-1">
                      Base processing: 5 credits + delivery fees per recipient
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(2)} className="border-border">
                  Back
                </Button>
                <Button
                  onClick={sendContent}
                  disabled={sending || !agreeTerms}
                  className="bg-accent-success hover:bg-accent-success/90 text-white"
                >
                  {sending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Send Content ({calculateCost()} credits)</>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 4: Complete */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <Card className="bg-accent-success/10 border-accent-success">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-accent-success/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-accent-success" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                    Content Delivered!
                  </h3>
                  {sentResults && (
                    <p className="text-text-secondary">
                      Successfully sent to {sentResults.successful} of {sentResults.total_contacts} recipient(s)
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Delivery Results */}
              {sentResults && sentResults.results && (
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-4">
                    <Label className="text-text-primary font-medium mb-3 block">Delivery Status</Label>
                    <div className="space-y-2">
                      {sentResults.results.map((result, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded bg-background border border-border">
                          <span className="text-sm text-text-primary">{result.contact_name || `Recipient ${i + 1}`}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            result.status === "sent" 
                              ? "bg-accent-success/20 text-accent-success" 
                              : "bg-accent-error/20 text-accent-error"
                          }`}>
                            {result.status === "sent" ? `✓ ${result.channel_used?.toUpperCase()}` : "Failed"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              {allowDelete && (
                <Button
                  variant="outline"
                  onClick={deleteContent}
                  className="w-full border-accent-error text-accent-error hover:bg-accent-error/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Request Content Deletion
                </Button>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCurrentStep(1);
                    setCreationMode(null);
                    setContentFile(null);
                    setContentPreview(null);
                    setModerationResult(null);
                    setRecipients([{ id: 1, name: "", contact: "", channel: "email" }]);
                    setMessage("");
                    setSentResults(null);
                    setAgreeTerms(false);
                  }}
                  className="flex-1 border-border"
                >
                  Create Another
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="flex-1 bg-primary hover:bg-primary-hover text-white"
                >
                  Back to Dashboard
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
